/* ============================================================
   Flow Wake-Up Quiz — Root App
   - Drives the screen state machine
   - Persists answers to localStorage
   - Computes the winning archetype via computeMatch
   - Mounts to #root
   Exposes: window.computeMatch (also used by bridge on the LP)
   ============================================================ */

(function () {
  var jsx = window.jsx;
  var useState = React.useState;
  var useEffect = React.useEffect;
  var useLayoutEffect = React.useLayoutEffect || React.useEffect;
  var useCallback = React.useCallback;
  var useRef = React.useRef;

  function haptic(method, options) {
    var h = window.FlowHaptics;
    if (h && typeof h[method] === 'function') h[method](options);
  }

  var STORAGE_KEY = window.__QUIZ_STORAGE_KEY || 'flow-quiz-wakeup';
  var OFFER_PATH = 'https://flowalarmclock.com/products/flow-alarm';
  var NO_EXIT_REPLAY_KEYS = {
    'interstitial:inter_understand_situation': true,
    'interstitial:inter_demo_alarm_rings': true,
    'interstitial:inter_demo_only_one_way': true,
    'interstitial:inter_demo_walk_to_dock': true,
    'interstitial:inter_demo_awake_ready': true
  };

  // ------------------------------------------------------------
  // computeMatch — the scoring algorithm
  // answers: { [questionId]: number | number[] }
  //   number   → single-select pick index
  //   number[] → multi-select pick indices
  // Returns: one of 'snoozer' | 'hostage' | 'inconsistent'
  // ------------------------------------------------------------
  function computeMatch(answers) {
    var scores = { snoozer: 0, hostage: 0, inconsistent: 0 };
    var QUIZ = window.QUIZ;
    if (!QUIZ) return 'snoozer';

    Object.keys(answers || {}).forEach(function (qid) {
      var q = QUIZ.questionById[qid];
      if (!q || q.type === 'interstitial') return;
      var picks = Array.isArray(answers[qid]) ? answers[qid] : [answers[qid]];
      picks.forEach(function (value) {
        var opt = resolveAnswerOption(q, value);
        if (!opt || !opt.scores) return;
        scores.snoozer      += opt.scores.snoozer      || 0;
        scores.hostage      += opt.scores.hostage      || 0;
        scores.inconsistent += opt.scores.inconsistent || 0;
      });
    });

    // Pick the highest scorer. Tie-break order: hostage > snoozer > inconsistent
    // because the bed-phone pattern is the most actionable and product-fit.
    // (Tie-breaks shouldn't be common but we want determinism.)
    var order = ['hostage', 'snoozer', 'inconsistent'];
    var winner = order[0];
    var best = -1;
    order.forEach(function (k) {
      if (scores[k] > best) {
        best = scores[k];
        winner = k;
      }
    });
    // Low-signal fallback — if the winning score is below threshold the user
    // doesn't strongly fit any failure pattern. Show the Steady Riser
    // diagnosis instead of forcing a weak archetype match.
    var threshold = (window.QUIZ && typeof window.QUIZ.lowSignalThreshold === 'number')
      ? window.QUIZ.lowSignalThreshold
      : 18;
    if (best < threshold) return 'steady';
    return winner;
  }
  window.computeMatch = computeMatch;

  function resolveAnswerOption(q, value) {
    if (!q || !q.options) return null;
    if (q.type === 'slider') {
      return q.options.find(function (opt) {
        var aboveMin = typeof opt.min !== 'number' || value >= opt.min;
        var belowMax = typeof opt.max !== 'number' || value <= opt.max;
        return aboveMin && belowMax;
      }) || null;
    }
    return q.options[value];
  }

  function formatAnswerLabel(q, value) {
    if (!q) return null;
    if (q.type === 'slider') {
      return value >= (q.max || 60) ? (q.max || 60) + '+ mins' : value + ' mins';
    }
    var opt = resolveAnswerOption(q, value);
    return opt && opt.label;
  }

  function slugifyAnalyticsValue(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/\+/g, ' plus ')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'unknown';
  }

  function getStepAnalytics(step, idx) {
    var stepType = step && step.kind ? step.kind : 'unknown';
    var stepId = step && step.id ? step.id : stepType;
    var analytics = {
      step_id: stepId,
      step_index: idx,
      step_type: stepType
    };

    if (stepType === 'question' && stepId) analytics.question_id = stepId;

    return analytics;
  }

  function getAnswerAnalytics(q, value) {
    var isMulti = Array.isArray(value);
    var values = isMulti ? value : [value];
    var answers = values.map(function (answerValue) {
      return formatAnswerLabel(q, answerValue);
    });
    var answerIds = values.map(function (answerValue, idx) {
      var opt = resolveAnswerOption(q, answerValue);
      var seed = opt && opt.id ? opt.id : (opt && opt.label ? opt.label : answerValue);
      return q.id + '_' + slugifyAnalyticsValue(seed || idx);
    });

    return {
      question_id: q.id,
      answer_id: isMulti ? answerIds : answerIds[0],
      answer: isMulti ? answers : answers[0],
      answer_index: value,
      is_multi_select: isMulti,
      // Kept while existing dashboards/debugging still reference old names.
      answer_idx: value,
      answer_label: isMulti ? answers : answers[0]
    };
  }

  function getPlainAnalyticsProps(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    if (value.nativeEvent || value.target || value.currentTarget) return {};
    return value;
  }

  function trackStepCompleted(step, idx, props) {
    if (!window.trackEvent || !step) return;
    window.trackEvent(
      'step_completed',
      Object.assign(getStepAnalytics(step, idx), getPlainAnalyticsProps(props))
    );
  }

  window.__quizScores = function (answers) {
    // Debug helper — surface raw scores for QA.
    var scores = { snoozer: 0, hostage: 0, inconsistent: 0 };
    var QUIZ = window.QUIZ;
    if (!QUIZ) return scores;
    Object.keys(answers || {}).forEach(function (qid) {
      var q = QUIZ.questionById[qid];
      if (!q || q.type === 'interstitial') return;
      var picks = Array.isArray(answers[qid]) ? answers[qid] : [answers[qid]];
      picks.forEach(function (value) {
        var opt = resolveAnswerOption(q, value);
        if (!opt || !opt.scores) return;
        scores.snoozer      += opt.scores.snoozer      || 0;
        scores.hostage      += opt.scores.hostage      || 0;
        scores.inconsistent += opt.scores.inconsistent || 0;
      });
    });
    return scores;
  };

  // ------------------------------------------------------------
  // State persistence
  // ------------------------------------------------------------
  function loadState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) { return null; }
  }
  function saveState(s) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (e) {}
  }

  function makeHistoryBaseState(currentState) {
    var next = {};
    if (currentState && typeof currentState === 'object') {
      Object.keys(currentState).forEach(function (key) {
        if (key.indexOf('flowQuizWakeup') !== 0) next[key] = currentState[key];
      });
    }
    next.flowQuizWakeup = true;
    next.flowQuizWakeupBase = true;
    next.flowQuizWakeupGuard = false;
    return next;
  }

  function makeHistoryGuardState(idx) {
    return {
      flowQuizWakeup: true,
      flowQuizWakeupBase: false,
      flowQuizWakeupGuard: true,
      idx: idx
    };
  }

  function isQuizManagedHistoryState(state) {
    return !!(state && state.flowQuizWakeup === true && (
      state.flowQuizWakeupBase === true ||
      state.flowQuizWakeupGuard === true
    ));
  }

  function isQuizGuardHistoryState(state) {
    return !!(state && state.flowQuizWakeup === true && state.flowQuizWakeupGuard === true);
  }

  // ------------------------------------------------------------
  // App
  // ------------------------------------------------------------
  function App(props) {
    var landingAssignment = props && props.landingAssignment ? props.landingAssignment : null;
    var saved = loadState();
    var idxRef = useState(saved && typeof saved.idx === 'number' ? saved.idx : 0);
    var idx = idxRef[0];
    var setIdx = idxRef[1];
    var previousIdxRef = useRef(idx);
    var latestIdxRef = useRef(idx);
    var historySyncRef = useRef({
      didSeed: false,
      guardActive: false,
      isRemovingGuard: false,
      isLeavingQuiz: false
    });
    latestIdxRef.current = idx;
    var navigationDirection = idx < previousIdxRef.current ? 'back' : 'forward';

    var answersRef = useState(saved && saved.answers ? saved.answers : {});
    var answers = answersRef[0];
    var setAnswers = answersRef[1];

    var winnerRef = useState(null);
    var winner = winnerRef[0];
    var setWinner = winnerRef[1];

    var SEQUENCE = window.QUIZ.sequence;
    var TOTAL_COUNTABLE = window.QUIZ.countableQuestions;

    // Count how many countable (question) steps are at-or-before current idx.
    var questionIdx = 0;
    for (var i = 0; i < idx && i < SEQUENCE.length; i++) {
      if (SEQUENCE[i].kind === 'question') questionIdx++;
    }

    // Persist on every step change.
    useEffect(function () {
      saveState({ idx: idx, answers: answers, winner: winner });
      previousIdxRef.current = idx;
    }, [idx, answers, winner]);

    // Keep one in-page guard entry above the real page entry. Every browser
    // Back pop consumes that guard and moves the quiz back one step; if there
    // are still quiz steps left, the guard is pushed again.
    useEffect(function () {
      if (!window.history || !window.history.replaceState || !window.history.pushState) return undefined;

      var sync = historySyncRef.current;

      function pushGuard(targetIdx) {
        window.history.pushState(makeHistoryGuardState(targetIdx), '', window.location.href);
        sync.guardActive = true;
      }

      try {
        window.history.replaceState(makeHistoryBaseState(window.history.state), '', window.location.href);
        sync.didSeed = true;
        sync.guardActive = false;
        if (latestIdxRef.current > 0) pushGuard(latestIdxRef.current);
      } catch (e) {
        return undefined;
      }

      function handlePopState(event) {
        if (!sync.didSeed || sync.isLeavingQuiz) return;
        if (sync.isRemovingGuard) {
          sync.isRemovingGuard = false;
          sync.guardActive = isQuizGuardHistoryState(event.state);
          return;
        }
        if (!isQuizManagedHistoryState(event.state)) return;

        sync.guardActive = isQuizGuardHistoryState(event.state);

        var currentIdx = latestIdxRef.current;
        if (currentIdx <= 0) {
          if (sync.guardActive) {
            sync.isRemovingGuard = true;
            try {
              window.history.back();
            } catch (e1) {
              sync.isRemovingGuard = false;
              sync.guardActive = false;
            }
          }
          return;
        }

        var nextIdx = Math.max(0, currentIdx - 1);
        if (nextIdx > 0) {
          try {
            pushGuard(nextIdx);
          } catch (e2) {
            sync.guardActive = false;
          }
        } else {
          sync.guardActive = false;
        }
        haptic('back', { key: 'browser-back', minInterval: 80 });
        latestIdxRef.current = nextIdx;
        setIdx(nextIdx);
      }

      window.addEventListener('popstate', handlePopState);
      return function () {
        window.removeEventListener('popstate', handlePopState);
      };
    }, []);

    useEffect(function () {
      if (!window.history || !window.history.pushState || !window.history.replaceState) return;
      var sync = historySyncRef.current;
      if (!sync.didSeed || sync.isLeavingQuiz) return;

      try {
        if (idx > 0) {
          if (!sync.guardActive && !sync.isRemovingGuard) {
            window.history.pushState(makeHistoryGuardState(idx), '', window.location.href);
            sync.guardActive = true;
          } else if (sync.guardActive && isQuizGuardHistoryState(window.history.state)) {
            window.history.replaceState(makeHistoryGuardState(idx), '', window.location.href);
          }
        } else if (sync.guardActive && !sync.isRemovingGuard) {
          sync.isRemovingGuard = true;
          window.history.back();
        }
      } catch (e) {}
    }, [idx]);

    var advance = useCallback(function (analyticsProps) {
      var currentIdx = latestIdxRef.current;
      trackStepCompleted(SEQUENCE[currentIdx], currentIdx, analyticsProps);
      setIdx(function (i) { return Math.min(i + 1, SEQUENCE.length - 1); });
    }, [SEQUENCE.length]);

    var handleStart = useCallback(function () {
      window.trackEvent('quiz_started', Object.assign(
        getStepAnalytics(SEQUENCE[0], 0),
        { pricing_variant: localStorage.getItem('flow-pv-wakeup') || null }
      ));
      advance();
    }, [advance, SEQUENCE]);

    // Visible back navigation is direct quiz state. Browser Back is handled
    // separately by the guard-state pop handler above.
    var goBack = useCallback(function () {
      setIdx(function (i) { return Math.max(0, i - 1); });
    }, []);

    var handleAnswer = useCallback(function (qid, value) {
      if (qid === 'wake_time' && typeof value === 'number') {
        try { sessionStorage.setItem('flow-wake-time-answer', String(value)); } catch (e) {}
      }

      setAnswers(function (curr) {
        var next = Object.assign({}, curr);
        next[qid] = value;
        return next;
      });
      var q = window.QUIZ.questionById[qid];
      var answerAnalytics = getAnswerAnalytics(q, value);
      var currentIdx = latestIdxRef.current;
      window.trackEvent('question_answered', Object.assign(
        getStepAnalytics(SEQUENCE[currentIdx], currentIdx),
        answerAnalytics
      ));
      advance(answerAnalytics);
    }, [advance]);

    var handleGate = useCallback(function (email) {
      // Email is already submitted by the gate; here we just lock in the
      // winner (computed at calibration enter) and advance to reveal.
      advance({ email_provided: Boolean(email) });
    }, [advance]);

    var handleSeePlan = useCallback(function () {
      var w = winner || computeMatch(answers);
      try { sessionStorage.setItem('flow-match', w); } catch (e) {}
      var currentIdx = latestIdxRef.current;
      var resultProps = { winner: w, result_id: w, recommended_result: w };
      trackStepCompleted(SEQUENCE[currentIdx], currentIdx, resultProps);
      window.trackEvent('quiz_completed', Object.assign(
        getStepAnalytics(SEQUENCE[currentIdx], currentIdx),
        resultProps
      ));
      historySyncRef.current.isLeavingQuiz = true;
      window.location.href = OFFER_PATH;
    }, [winner, answers, SEQUENCE]);

    var handleShare = useCallback(function () {
      if (navigator.share) {
        navigator.share({
          title: "I'm a " + (window.ARCHETYPES[winner] || {}).name,
          text: "Flow diagnosed my wake-up pattern. Take the quiz:",
          url: window.location.origin + '/pages/quiz-wakeup'
        }).catch(function () { /* swallow */ });
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(window.location.origin + '/pages/quiz-wakeup');
      }
    }, [winner]);

    var step = SEQUENCE[idx];

    useEffect(function () {
      if (!step || !window.trackEvent) return;
      window.trackEvent('step_viewed', getStepAnalytics(step, idx));
    }, [idx, step]);

    var content;
    if (!step) {
      content = null;
    } else if (step.kind === 'landing') {
      content = jsx(window.LandingScreen, { landingAssignment: landingAssignment, onStart: handleStart });
    } else if (step.kind === 'question') {
      var q = window.QUIZ.questionById[step.id];
      // key on the question id forces React to remount the screen between
      // questions — without it the QuestionScreen's local `selected` state
      // would carry over from the previous question.
      content = jsx(window.QuestionScreen, {
        key: 'q-' + q.id,
        question: q,
        initialValue: answers[q.id],
        onAnswer: function (v) { handleAnswer(q.id, v); }
      });
    } else if (step.kind === 'interstitial') {
      var inter = window.QUIZ.questionById[step.id];
      content = jsx(window.InterstitialScreen, {
        key: 'i-' + inter.id,
        interstitial: inter,
        answers: answers,
        navigationDirection: navigationDirection,
        onContinue: advance
      });
    } else if (step.kind === 'calibration') {
      content = jsx(window.CalibrationScreen, {
        key: 'cal',
        answers: answers,
        onComplete: function () {
          var w = computeMatch(answers);
          setWinner(w);
          try { sessionStorage.setItem('flow-match', w); } catch (e) {}
          advance({ result_id: w, recommended_result: w });
        }
      });
    } else if (step.kind === 'gate') {
      var w1 = winner || computeMatch(answers);
      content = jsx(window.EmailGateScreen, {
        winner: w1,
        answers: answers,
        onSubmit: handleGate,
        onSkip: handleGate
      });
    } else if (step.kind === 'unlock') {
      content = jsx(window.UnlockScreen, {
        key: 'unlock',
        onUnlock: advance
      });
    } else if (step.kind === 'scratch') {
      var wScratch = winner || computeMatch(answers);
      content = jsx(window.ScratchCardScreen, {
        key: 'scratch',
        winner: wScratch,
        onScratched: function () {
          advance({ result_id: wScratch, recommended_result: wScratch });
        }
      });
    } else if (step.kind === 'reveal') {
      var w2 = winner || computeMatch(answers);
      content = jsx(window.RevealScreen, {
        winner: w2,
        answers: answers,
        // Reveal CTA now advances to the dock_showcase step instead of
        // redirecting directly to the LP — there's one more product beat.
        onSeePlan: function () {
          advance({ result_id: w2, recommended_result: w2 });
        },
        onShare: handleShare
      });
    } else if (step.kind === 'dock_showcase') {
      var wDock = winner || computeMatch(answers);
      content = jsx(window.DockShowcaseScreen, {
        key: 'dock',
        winner: wDock,
        // The dock_showcase's "See Flow Dock" CTA is the final beat —
        // it sends the user to the Flow Dock PDP.
        onContinue: handleSeePlan
      });
    }

    // Show progress bar on questions + interstitials (NOT on landing / gate / reveal).
    var isSituationIntro = step && step.kind === 'interstitial' && step.id === 'inter_understand_situation';
    var showProgress = step && !isSituationIntro && (step.kind === 'question' || step.kind === 'interstitial' || step.kind === 'calibration');
    // Back button visible on questions + interstitials only. Locked forward
    // on landing (no previous), calibration, gate, scratch, reveal, and
    // dock_showcase — those are points the user shouldn't unwind.
    var canGoBack = idx > 0 && step && !isSituationIntro && (step.kind === 'question' || step.kind === 'interstitial');

    // Key used by TransitionStack to detect when content changes.
    // Combines step kind + step id so even adjacent same-kind steps
    // (consecutive questions) trigger a cross-fade.
    var stepKey = step ? (step.kind + ':' + (step.id || idx)) : 'empty';

    return jsx('div', {
      className: 'app-viewport',
      children: jsx('div', {
        className: 'phone-frame',
        children: jsx('div', {
          className: 'shell',
          children: [
            jsx(window.BrandHeader, {
              showProgress: showProgress,
              questionIdx: questionIdx,
              total: TOTAL_COUNTABLE,
              canGoBack: canGoBack,
              onBack: goBack
            }),
            jsx('div', {
              className: 'stage',
              children: jsx(TransitionStack, { stepKey: stepKey, skipExitKeys: NO_EXIT_REPLAY_KEYS, children: content })
            })
          ]
        })
      })
    });
  }

  // ------------------------------------------------------------
  // TransitionStack — iOS-style cross-fade
  // When `stepKey` changes, the previous children get the .cross-exit
  // animation, the new children get .cross-enter. Both render in the
  // same grid cell so they overlap during the fade. The exit unmounts
  // after the longest animation finishes (360ms enter + 60ms buffer).
  // ------------------------------------------------------------
  function TransitionStack(props) {
    var stateRef = useState({ key: props.stepKey, current: props.children, exiting: null, exitKey: null });
    var state = stateRef[0];
    var setState = stateRef[1];

    useLayoutEffect(function () {
      if (props.stepKey === state.key) {
        // Same step — just update current content in case it re-rendered
        // (e.g. selection state inside a multi-select question).
        if (props.children !== state.current) {
          setState({ key: state.key, current: props.children, exiting: state.exiting, exitKey: state.exitKey });
        }
        return;
      }
      // Step changed — promote current to exiting, swap in new.
      var skipExit = props.skipExitKeys && props.skipExitKeys[state.key];
      haptic('selection', { key: 'screen-enter', minInterval: 140 });
      setState({
        key: props.stepKey,
        current: props.children,
        exiting: skipExit ? null : state.current,
        exitKey: skipExit ? null : state.key
      });
      if (skipExit) return;

      var t = setTimeout(function () {
        setState(function (curr) {
          // Only clear if no newer transition kicked in.
          if (curr.exitKey === state.key) return { key: curr.key, current: curr.current, exiting: null, exitKey: null };
          return curr;
        });
      }, 420);
      return function () { clearTimeout(t); };
    }, [props.stepKey, props.children, props.skipExitKeys]);

    return jsx('div', {
      className: 'transition-stack',
      children: [
        state.exiting
          ? jsx('div', { key: 'exit-' + state.exitKey, className: 'cross-exit', 'aria-hidden': true, children: state.exiting })
          : null,
        jsx('div', { key: 'enter-' + state.key, className: 'cross-enter', children: state.current })
      ]
    });
  }

  // ------------------------------------------------------------
  // Mount
  // ------------------------------------------------------------
  var didMount = false;

  function mount() {
    var root = document.getElementById('root');
    if (!root) return;
    if (!window.QUIZ || !window.LandingScreen) {
      // One of the dependent scripts hasn't loaded yet (defer order should
      // make this impossible, but be safe).
      setTimeout(mount, 30);
      return;
    }
    if (didMount) return;
    didMount = true;

    function renderApp(landingAssignment) {
      ReactDOM.createRoot(root).render(jsx(App, { landingAssignment: landingAssignment }));
    }

    if (
      window.FlowQuizExperiments &&
      typeof window.FlowQuizExperiments.resolveLandingAssignment === 'function'
    ) {
      window.FlowQuizExperiments.resolveLandingAssignment()
        .then(renderApp)
        .catch(function () {
          renderApp(null);
        });
      return;
    }

    renderApp(null);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
